import React, { useEffect, useRef, useState } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getResolvedImageUrl, setResolvedImageUrl } from '../utils/imageCache';

interface LazyFirebaseImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  pathOrUrl: string;
}

const TRANSPARENT_PLACEHOLDER_SVG =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>';

const LazyFirebaseImage: React.FC<LazyFirebaseImageProps> = ({
  pathOrUrl,
  alt,
  loading = 'lazy',
  decoding = 'async',
  ...imgProps
}) => {
  const imageElementRef = useRef<HTMLImageElement | null>(null);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [hasStartedResolving, setHasStartedResolving] = useState<boolean>(false);

  useEffect(() => {
    setResolvedSrc(null);
    setHasStartedResolving(false);
  }, [pathOrUrl]);

  useEffect(() => {
    const element = imageElementRef.current;
    if (!element) return;

    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      // Fallback: resolve immediately
      resolveSource();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            resolveSource();
            if (element) observer.unobserve(element);
          }
        });
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);

    return () => {
      try {
        if (element) observer.unobserve(element);
      } catch (_) {
        // ignore
      }
    };

    async function resolveSource() {
      if (hasStartedResolving) return;
      setHasStartedResolving(true);
      try {
        if (!pathOrUrl) return;
        const cached = getResolvedImageUrl(pathOrUrl);
        if (cached) {
          setResolvedSrc(cached);
          return;
        }
        if (pathOrUrl.startsWith('http')) {
          setResolvedSrc(pathOrUrl);
        } else {
          const storage = getStorage();
          const imageRef = ref(storage, pathOrUrl);
          const url = await getDownloadURL(imageRef);
          setResolvedSrc(url);
          setResolvedImageUrl(pathOrUrl, url);
        }
      } catch (error) {
        // Leave placeholder on error
        // Optionally, you could set a fallback image here
      }
    }
  }, [pathOrUrl, hasStartedResolving]);

  return (
    <img
      ref={imageElementRef}
      src={resolvedSrc || TRANSPARENT_PLACEHOLDER_SVG}
      alt={alt}
      loading={loading}
      decoding={decoding as any}
      {...imgProps}
    />
  );
};

export default LazyFirebaseImage;


